import { useLingui } from "@lingui/react";
import { Button, Modal, TextArea, Toggle } from "@warp-ds/react";
import { useEffect, useState } from "react";

enum SurveyOptions {
  moreInfo = "moreInfo",
  infoWarranties = "infoWarranties",
  credibility = "credibility",
  contactInfo = "contactInfo",
  otherCars = "otherCars",
  other = "other",
}

type Props = {
  showModal: boolean;
  toggleModal: (val: boolean) => void;
  featureToggle: boolean;
};

export const HomepageSurvey = ({ showModal, toggleModal, featureToggle }: Props) => {
  const { i18n } = useLingui();
  const [data, setData] = useState(Object.fromEntries(Object.values(SurveyOptions).map((option) => [option, false])));
  const [textReason, setTextReason] = useState("");
  const [invalidReason, setInvalidReason] = useState(false);
  useEffect(() => {
    const hasSurveyed = localStorage.getItem("mobility-homepage-survey-done");
    if (featureToggle && !hasSurveyed) {
      toggleModal(showModal);
    } else {
      toggleModal(false);
    }
  }, [featureToggle, showModal, toggleModal]);

  const handleDismiss = () => {
    localStorage.setItem("mobility-homepage-survey-done", "true");
    toggleModal(false);
  };

  const handleCheckbox = (value: { label: string; value: SurveyOptions }) => {
    setData((prevData) => ({
      ...prevData,
      [value.value]: !prevData[value.value],
    }));
    setInvalidReason(false);
  };

  const handleSubmit = async () => {
    if (!Object.values(data).some(Boolean)) {
      setInvalidReason(true);
      return;
    }

    try {
      await fetch("/mobility/api/survey/homepage", {
        method: "POST",
        body: JSON.stringify({ ...data, text: textReason }),
        headers: {
          "Content-Type": "application/json",
        },
      });
    } catch (error) {
      console.error("Error submitting survey:", error);
    } finally {
      localStorage.setItem("mobility-homepage-survey-done", "true");
      toggleModal(false);
    }
  };

  const radioOptions = [
    {
      value: SurveyOptions.moreInfo,
      label: i18n._({
        id: "survey.dealerHomepage.option.moreInfo",
        message: "More details about the car (e.g. specs, condition, service history)",
      }),
    },
    {
      value: SurveyOptions.infoWarranties,
      label: i18n._({
        id: "survey.dealerHomepage.option.infoWarranties",
        message: "Warranty information (what’s included, terms, etc.)",
      }),
    },
    {
      value: SurveyOptions.credibility,
      label: i18n._({
        id: "survey.dealerHomepage.option.credibility",
        message: "Information to evaluate the dealer’s credibility (e.g. reviews, certifications)",
      }),
    },
    {
      value: SurveyOptions.contactInfo,
      label: i18n._({
        id: "survey.dealerHomepage.option.contactInfo",
        message: "Contact info or opening hours",
      }),
    },
    {
      value: SurveyOptions.otherCars,
      label: i18n._({
        id: "survey.dealerHomepage.option.otherCars",
        message: "Other vehicles for sale from the dealer",
      }),
    },
    { value: SurveyOptions.other, label: i18n._({ id: "survey.dealerHomepage.option.other", message: "Other" }) },
  ];

  return (
    <Modal
      right
      open={showModal}
      onDismiss={handleDismiss}
      title={i18n._({ id: "survey.dealerHomepage.surveyTitle", message: "Quick question before you continue" })}
      style={{
        //@ts-expect-error - this is a custom prop
        "--w-modal-max-height": "fit-content",
        "--w-modal-height": "100%",
      }}
    >
      <Toggle
        type="checkbox"
        onChange={(selected) => handleCheckbox(selected)}
        title={i18n._({
          id: "survey.dealerHomepage.reasonsTitle",
          message: "What were you hoping to find on the dealer’s website?",
        })}
        helpText={
          invalidReason
            ? i18n._({
                id: "survey.dealerHomepage.validateReason.helpText",
                message:
                  "Please select an option before submitting. If you prefer not to answer, you can simply close this popup.",
              })
            : ""
        }
        options={radioOptions}
        invalid={invalidReason}
      />
      {data.other ? (
        <TextArea
          label={i18n._({
            id: "survey.dealerHomepage.textArea.helpText",
            message: "Please describe what you were looking for",
          })}
          value={textReason}
          onChange={(event) => setTextReason(event.target.value)}
        />
      ) : (
        <></>
      )}
      <div className="mt-6 flex justify-end">
        <Button primary type="submit" onClick={handleSubmit} className="mr-0 mt-16">
          {i18n._({ id: "common.submit", message: "Submit" })}
        </Button>
      </div>
    </Modal>
  );
};
